/**
 * Keeps React from crashing on a page the browser has translated.
 *
 * Page translation (Chrome's, and Google Translate on iOS) swaps each text node for a `<font>`
 * wrapper behind React's back. When React later removes or inserts around the node it still holds,
 * the DOM throws "NotFoundError: The object can not be found here" and the whole screen falls to
 * the error boundary — seen on the dashboard from a Thai-locale iPhone (Sentry 146861160).
 *
 * Only the two cases translation produces are softened: a node moved deeper under the same parent,
 * or one already detached. Anything else still goes to the browser and throws as before.
 */
export function installTranslatedDomGuard(): void {
  const removeChild = Node.prototype.removeChild;
  const insertBefore = Node.prototype.insertBefore;

  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      if (child.parentNode === null) return child;
      if (this.contains(child)) return removeChild.call(child.parentNode, child) as T;
    }
    return removeChild.call(this, child) as T;
  };

  Node.prototype.insertBefore = function <T extends Node>(this: Node, node: T, ref: Node | null): T {
    let anchor = ref;
    while (anchor && anchor.parentNode !== this) anchor = anchor.parentNode;
    return insertBefore.call(this, node, anchor) as T;
  };
}
